import { Inject, Injectable } from '@angular/core';
import { FeatureFlagService } from '@ddv/entitlements';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { Observable, switchMap, map, of } from 'rxjs';

import { emptySharing, fromLegacy, isShared, LegacySharing, Sharing, toPermissions } from '../models/sharing';

@Injectable()
export class DashboardSharingService {
    constructor(
        @Inject(ApiServices.ddvMW) private readonly legacyMW: ApiExecutorService,
        @Inject(ApiServices.ddvApi) private readonly api: ApiExecutorService,
        private readonly featureFlags: FeatureFlagService,
    ) {}

    updateSharing(
        clientCode: string,
        dashboardId: number | string,
        sharing: Sharing | undefined,
        dashboardClientCode?: string, // this should be removed after we have fully migrated dashboards
    ): Observable<void> {
        return this.featureFlags.isFlagEnabled('use-aws-for-sharing', clientCode)
            .pipe(
                switchMap((isEnabled) => {
                    if (!isEnabled) {
                        return this.updateSharingLegacy(clientCode, dashboardId, sharing);
                    }

                    const urlBase = `dashboards/${dashboardId}/sharing`;
                    const url = dashboardClientCode ? `${urlBase}?dashboardClientCode=${dashboardClientCode}` : urlBase;

                    return this.api.invokeServiceWithBody<void, Sharing>(
                        clientCode,
                        url,
                        'PUT',
                        sharing ?? emptySharing());
                }),
            );
    }

    isDashboardShared(clientCode: string, dashboardId: number | string): Observable<boolean> {
        return this.getSharing(clientCode, dashboardId)
            .pipe(
                map((result) => isShared(result)),
            );
    }

    getSharing(clientCode: string, dashboardId: number | string | undefined): Observable<Sharing> {
        if (dashboardId === undefined) {
            return of(emptySharing());
        }

        return this.featureFlags.isFlagEnabled('use-aws-for-sharing', clientCode)
            .pipe(
                switchMap((isEnabled) => {
                    if (!isEnabled) {
                        return this.getSharingLegacy(clientCode, dashboardId);
                    }

                    return this.api.invokeService<Sharing>(
                        clientCode,
                        `dashboards/${dashboardId}/sharing`,
                        'GET');
                }),
            );
    }

    private getSharingLegacy(clientCode: string, dashboardId: number | string): Observable<Sharing> {
        return this.legacyMW.invokeService<LegacySharing>(
            clientCode,
            `dashboard/${dashboardId}/sharing`,
            'GET')
            .pipe(
                map((result) => fromLegacy(result)),
            );
    }

    private updateSharingLegacy(
        clientCode: string,
        dashboardId: string | number,
        sharing: Sharing | undefined,
    ): Observable<void> {
        return this.legacyMW.invokeServiceWithBody<void>(
            clientCode,
            `dashboard/${dashboardId}/sharing`,
            'POST',
            { dashboardPermissions: sharing ? toPermissions(sharing) : undefined });
    }
}
